/**
 * ShareView — Public read-only view of a shared RCA assessment.
 * No authentication required. Validates token via /api/share directly,
 * then computes scores from answers using the scoring engine.
 */

import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { RiskLevel, PhaseId, RCACategory } from '@/types/rca'
import { validateShareTokenPublic } from '@/services/supabaseService'
import { computeOverall, computePhaseCategory } from '@/services/rcaScoring'
import { PHASES } from '@/data/rcaQuestions'

const PHASE_LABELS: Record<PhaseId, string> = {
  pre_charter: 'Pre-Charter',
  initiation: 'Initiation',
  planning: 'Planning',
  event_driven: 'Event-Driven',
}

type BadgeVariant = 'destructive' | 'default' | 'secondary' | 'outline'

function riskBadge(level: RiskLevel | null): BadgeVariant {
  if (level === 'High') return 'destructive'
  if (level === 'Medium') return 'default'
  if (level === 'Low') return 'secondary'
  return 'outline'
}

function categoryLabel(cat: RCACategory | null): string {
  switch (cat) {
    case 1: return 'Category 1 — Standard Project'
    case 2: return 'Category 2 — Moderate Project'
    case 3: return 'Category 3 — Complex Project'
    case 4: return 'Category 4 — Critical Project'
    default: return 'Not Categorised'
  }
}

function categoryDescription(cat: RCACategory | null): string {
  switch (cat) {
    case 1: return 'Low risk and low complexity. Standard project management practices apply.'
    case 2: return 'Moderate risk or complexity. Enhanced oversight and structured governance recommended.'
    case 3: return 'High risk or complexity. Formal governance, dedicated PM, and steering committee required.'
    case 4: return 'High risk and high complexity. Executive sponsorship, full PMO involvement, and rigorous governance required.'
    default: return ''
  }
}

interface ComputedResults {
  projectName: string
  department: string
  createdAt: string
  activePhases: PhaseId[]
  overall: ReturnType<typeof computeOverall>
  phases: Array<{ phaseId: PhaseId; result: ReturnType<typeof computePhaseCategory> }>
}

function LoadingSkeleton() {
  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-5 w-64" />
      <div className="grid grid-cols-2 gap-4">
        <Skeleton className="h-24 rounded-xl" />
        <Skeleton className="h-24 rounded-xl" />
      </div>
      <Skeleton className="h-40 rounded-xl" />
      <Skeleton className="h-56 rounded-xl" />
    </div>
  )
}

function InvalidToken() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="max-w-md text-center">
        <div className="mb-4 text-5xl">🔒</div>
        <h1 className="mb-2 text-2xl font-bold text-slate-800">Link expired or invalid</h1>
        <p className="mb-6 text-slate-500">
          This shared assessment link has expired or is no longer valid.
        </p>
        <Link
          to="/login"
          className="rounded-md bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Sign in to RCA Tool
        </Link>
      </div>
    </div>
  )
}

export default function ShareView() {
  const { token } = useParams<{ token: string }>()
  const [results, setResults] = useState<ComputedResults | null>(null)
  const [status, setStatus] = useState<'loading' | 'invalid' | 'ready'>('loading')

  useEffect(() => {
    if (!token) { setStatus('invalid'); return }

    let cancelled = false

    validateShareTokenPublic(token).then((payload) => {
      if (cancelled) return
      if (!payload?.assessment) { setStatus('invalid'); return }

      const { project, assessment } = payload
      const answers = assessment.answers ?? {}

      // Determine active phases from the answers keys
      const phaseSet = new Set<PhaseId>()
      Object.keys(answers).forEach((key) => {
        const phaseId = key.split('_risk_')[0]?.split('_complexity_')[0] as PhaseId
        if (phaseId) phaseSet.add(phaseId)
      })
      const activePhases = (['pre_charter', 'initiation', 'planning', 'event_driven'] as PhaseId[])
        .filter((p) => phaseSet.has(p))

      const overall = computeOverall(activePhases, answers, PHASES)

      const phases = activePhases.map((phaseId) => ({
        phaseId,
        result: computePhaseCategory(phaseId, activePhases, answers, PHASES),
      }))

      setResults({
        projectName: project?.name ?? assessment.project_id ?? 'Assessment',
        department: project?.department ?? '',
        createdAt: assessment.created_at,
        activePhases,
        overall,
        phases,
      })
      setStatus('ready')
    }).catch(() => {
      if (!cancelled) setStatus('invalid')
    })

    return () => { cancelled = true }
  }, [token])

  if (status === 'loading') return <LoadingSkeleton />
  if (status === 'invalid') return <InvalidToken />

  const r = results!

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white px-6 py-4">
        <div className="mx-auto flex max-w-3xl items-center justify-between">
          <img
            src="/logo-primary.svg"
            alt="RCA Tool"
            className="h-10 w-auto"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
          />
          <Badge variant="secondary" className="text-xs font-semibold uppercase tracking-wide">
            Shared Assessment
          </Badge>
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-6 px-6 py-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{r.projectName}</h1>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500">
            {r.department && <span>{r.department}</span>}
            {r.department && <span>·</span>}
            <span>
              {new Date(r.createdAt).toLocaleDateString('en-US', {
                year: 'numeric', month: 'long', day: 'numeric',
              })}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500 uppercase tracking-wide">
                Overall Risk
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Badge variant={riskBadge(r.overall.riskLevel)} className="text-base px-3 py-1">
                {r.overall.riskLevel}
              </Badge>
              <p className="mt-2 text-sm text-slate-500">Score: {r.overall.avgRisk.toFixed(1)}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500 uppercase tracking-wide">
                Overall Complexity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Badge variant={riskBadge(r.overall.complexityLevel)} className="text-base px-3 py-1">
                {r.overall.complexityLevel}
              </Badge>
              <p className="mt-2 text-sm text-slate-500">Score: {r.overall.avgComplexity.toFixed(1)}</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500 uppercase tracking-wide">
              Project Category
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-semibold text-slate-800">{categoryLabel(r.overall.category)}</p>
            <p className="mt-1 text-sm text-slate-500">{categoryDescription(r.overall.category)}</p>
          </CardContent>
        </Card>

        {r.phases.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500 uppercase tracking-wide">
                Phase Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Phase</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Risk</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Complexity</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Category</th>
                  </tr>
                </thead>
                <tbody>
                  {r.phases.map(({ phaseId, result: pr }) => (
                    <tr key={phaseId} className="border-b border-slate-100 last:border-0">
                      <td className="px-4 py-3 font-medium text-slate-700">
                        {PHASE_LABELS[phaseId]}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={riskBadge(pr.riskLevel)} className="text-xs">
                          {pr.riskLevel}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        {phaseId === 'event_driven'
                          ? <span className="text-slate-400 text-xs">N/A</span>
                          : <Badge variant={riskBadge(pr.complexityLevel)} className="text-xs">{pr.complexityLevel}</Badge>
                        }
                      </td>
                      <td className="px-4 py-3 text-slate-600">Cat. {pr.category}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </main>

      <footer className="border-t border-slate-200 py-6 text-center text-xs text-slate-400">
        Generated by Risk &amp; Complexity Assessment Tool
      </footer>
    </div>
  )
}
